import { extendZodWithOpenApi } from '@anatine/zod-openapi'
import z from 'zod'
import { Brand, HistoryTables } from '../../shared'
import { FeedingPlanType } from '../common'
import { DiscountInterval } from '../pricing/types'
import { FeedingPlanServingSize, FeedingPlanStatus, PlanStrategy, PriceStrategy, SubscriptionStatus } from './types'
import { ProductType } from '../product/schemas'
import {
  generateRequiredIdVersionError,
  idVersionMustNotExist,
} from './utils'

extendZodWithOpenApi(z)

export const CreateEntityOmitFields = {
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  version: true,
} as const

const UpdateEntityOmitFields = {
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
} as const

export const BaseSchema = z.object({
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  deletedAt: z.string().datetime().optional().nullable(),
  version: z.number().int().gte(0),
})

export type BaseSchema = z.infer<typeof BaseSchema>

export const ExtraSchema = BaseSchema.extend({
  id: z.string().uuid(),
  productVariantId: z.string().uuid(),
  type: z.nativeEnum(ProductType),
  startOrderIndex: z.number().int().gte(0),
  nthOrder: z.number().int().gte(0),
  quantity: z.number().int().gte(0),
  price: z.number().int().nonnegative(),
  cancelledAt: z.string().datetime().optional().nullable(),
  cancelReasonId: z.string().uuid().optional().nullable(),
  cancelNotes: z.string().optional().nullable(),
  cancelledByAdmin: z.boolean().optional().nullable(),
})

export type ExtraSchema = z.infer<typeof ExtraSchema>

export const FeedingPlanSkuSchema = BaseSchema.extend({
  id: z.string().uuid(),
  productVariantId: z.string().uuid(),
  ratio: z.number().int().gte(0),
  price: z.number().int().nonnegative(),
})

export type FeedingPlanSkuSchema = z.infer<typeof FeedingPlanSkuSchema>

export const FeedingPlanSchema = BaseSchema.extend({
  id: z.string().uuid(),
  status: z.nativeEnum(FeedingPlanStatus),
  isEligible: z.boolean().optional(),
  startOrderIndex: z.number(),
  type: z.nativeEnum(FeedingPlanType),
  animalId: z.string().uuid(),
  currentDailyServingSizeInGrams: z.nativeEnum(FeedingPlanServingSize),
  currentShippedDailyServingSizeInGrams: z.number().gte(0),
  currentPouchSizeInGrams: z.number().int().gte(0),
  puppyServingSize: z.number().int().gte(0).nullable().describe('Puppy feature - Puppy serving size, if plan is attached to a puppy'),
  puppyAdultServingSize: z.number().int().gte(0).nullable().describe('Puppy feature - Expected adult serving size, assuming when puppy become adult'),
  totalPouches: z.number().int().gte(0),
  totalPrice: z.number().int().nonnegative(),
  dailyPrice: z.number().int().nonnegative(),
  skus: z.array(FeedingPlanSkuSchema).default([]),
})

export type FeedingPlanSchema = z.infer<typeof FeedingPlanSchema>

export const SharedFeedingPlanAnimalSchema = BaseSchema.extend({
  id: z.string().uuid(),
  animalId: z.string().uuid(),
  dailyPouches: z.number().nonnegative(),
  dailyEnergyKilocalories: z.number().int().optional().nullable(),
  minDailyEnergyKilocalories: z.number().int().optional().nullable(),
})

export type SharedFeedingPlanAnimalSchema = z.infer<typeof SharedFeedingPlanAnimalSchema>

export const SharedFeedingPlanSchema = BaseSchema.extend({
  id: z.string().uuid(),
  status: z.nativeEnum(FeedingPlanStatus),
  startOrderIndex: z.number(),
  type: z.nativeEnum(FeedingPlanType),
  dailyPrice: z.number().int().nonnegative(),
  skus: z.array(FeedingPlanSkuSchema).default([]),
  animals: z.array(SharedFeedingPlanAnimalSchema).default([]),
})

export type SharedFeedingPlanSchema = z.infer<typeof SharedFeedingPlanSchema>

export const CancelSubscriptionSchema = z.object({
  primaryCancelReasonId: z.string().uuid(),
  secondaryCancelReasonId: z.string().uuid().optional().nullable(),
  otherCancelReason: z.string().optional().nullable(),
  cancellingUserId: z.string().uuid().describe('The user id of the person cancelling the subscription'),
  cancelledByAdmin: z.boolean().optional().nullable(),
})

export const PauseSubscriptionBodySchema = z.object({
  pauseReasonId: z.string().uuid().optional().nullable(),
  otherPauseReason: z.string().optional().nullable(),
  pausedByAdmin: z.boolean().optional().nullable(),
})

export type PauseSubscriptionBody = z.infer<typeof PauseSubscriptionBodySchema>

export const SubscriptionCancellationReasonSchema = CancelSubscriptionSchema
  .omit({ cancellingUserId: true })
  .extend({
    cancelledDate: z.string().datetime(),
    reactivationDate: z.string().datetime().optional().nullable(),
  })

export type SubscriptionCancellationReasonSchema = z.infer<typeof SubscriptionCancellationReasonSchema>

export const SubscriptionPausingReasonSchema = PauseSubscriptionBodySchema
  .extend({
    pausedDate: z.string().datetime(),
    unpausedDate: z.string().datetime().optional().nullable(),
  })

export type SubscriptionPausingReasonSchema = z.infer<typeof SubscriptionPausingReasonSchema>

export const SubscriptionDiscountSchema = z.object({
  id: z.string().uuid(),
  discountId: z.string().uuid(),
  orderStartIndex: z.number().int().nonnegative(),
  discountInterval: z.nativeEnum(DiscountInterval),
  orderQuantity: z.number().int().min(0).nullable(),
})

export type SubscriptionDiscountSchema = z.infer<typeof SubscriptionDiscountSchema>

export const SubscriptionSchema = BaseSchema.extend({
  id: z.string().uuid(),
  status: z.nativeEnum(SubscriptionStatus),
  userId: z.string().uuid(),
  preferredDeliverySlotId: z.string(),
  cadenceInWeeks: z.number().int().gte(0),
  customCadenceInWeeks: z.number().int().gte(0).nullish(),
  customBoxInWeeks: z.number().int().gte(0).nullish(),
  feedingPlanTotalPrice: z.number().int().nonnegative(),
  feedingPlanTotalPouches: z.number().int().gte(0),
  lastProcessedOrderIndex: z.number().int().gte(0),
  extras: z.array(ExtraSchema).default([]),
  feedingPlans: z.array(FeedingPlanSchema).default([]),
  sharedFeedingPlan: SharedFeedingPlanSchema.optional().nullable(),
  note: z.string().optional().nullable(),
  startAt: z.union([
    z.string().date(),
    z.date(),
  ]).transform(d => d instanceof Date ? d.toISOString().split('T')[0] : d).optional().nullable(),
  pauseAt: z.union([
    z.string().date(),
    z.date(),
  ]).transform(d => d instanceof Date ? d.toISOString().split('T')[0] : d).optional().nullable(),
  subscriptionCancellationReasons: SubscriptionCancellationReasonSchema.array().default([]),
  subscriptionPausingReasons: SubscriptionPausingReasonSchema.array().default([]),
  cancelledAt: z.string().datetime().optional().nullable(),
  discounts: SubscriptionDiscountSchema.array().default([]),
  planStrategy: z.nativeEnum(PlanStrategy).default(PlanStrategy.FEEDING_PLAN),
  priceStrategy: z.nativeEnum(PriceStrategy).default(PriceStrategy.VOLUMETRIC_LYKA_V1),
  brand: z.nativeEnum(Brand).default(Brand.LYKA),
  introductorySubscriptionType: z.nativeEnum(FeedingPlanType).optional().nullable(),
})

export type SubscriptionSchema = z.infer<typeof SubscriptionSchema>

export const CreateSubscriptionBodyExtraSchema = ExtraSchema.omit(CreateEntityOmitFields).and(z.object({
  id: z.string().uuid().optional()
}))

export type CreateSubscriptionBodyExtra = z.infer<typeof CreateSubscriptionBodyExtraSchema>

export const CreateSubscriptionBodyFeedingPlanSkuSchema = FeedingPlanSkuSchema.omit(CreateEntityOmitFields)
export type CreateSubscriptionBodyFeedingPlanSku = z.infer<typeof CreateSubscriptionBodyFeedingPlanSkuSchema>

export const CreateSubscriptionBodyFeedingPlanSchema = FeedingPlanSchema
  .omit(CreateEntityOmitFields)
  .omit({
    startOrderIndex: true,
    currentPouchSizeInGrams: true,
    currentShippedDailyServingSizeInGrams: true,
    isEligible: true,
  })
  .extend({
    startOrderIndex: FeedingPlanSchema.shape.startOrderIndex.optional(),
    skus: z.array(CreateSubscriptionBodyFeedingPlanSkuSchema).min(1),
  })
export type CreateSubscriptionBodyFeedingPlan = z.infer<typeof CreateSubscriptionBodyFeedingPlanSchema>

export const CreateSubscriptionBodySharedFeedingPlanAnimalSchema = SharedFeedingPlanAnimalSchema.omit(CreateEntityOmitFields)
export type CreateSubscriptionBodySharedFeedingPlanAnimal = z.infer<typeof CreateSubscriptionBodySharedFeedingPlanAnimalSchema>

export const CreateSubscriptionBodySharedFeedingPlanSchema = SharedFeedingPlanSchema
  .omit(CreateEntityOmitFields)
  .omit({
    startOrderIndex: true,
  })
  .extend({
    startOrderIndex: SharedFeedingPlanSchema.shape.startOrderIndex.optional(),
    skus: z.array(CreateSubscriptionBodyFeedingPlanSkuSchema).min(1),
    animals: z.array(CreateSubscriptionBodySharedFeedingPlanAnimalSchema).min(1),
  })
export type CreateSubscriptionBodySharedFeedingPlan = z.infer<typeof CreateSubscriptionBodySharedFeedingPlanSchema>

export const CreateSubscriptionBodySchema = SubscriptionSchema.pick({
  userId: true,
  preferredDeliverySlotId: true,
  cadenceInWeeks: true,
  feedingPlanTotalPrice: true,
  feedingPlanTotalPouches: true,
  note: true,
  startAt: true,
  planStrategy: true,
  priceStrategy: true,
  brand: true,
}).extend({
  status: z.enum([SubscriptionStatus.draft, SubscriptionStatus.active]),
  extras: z.array(CreateSubscriptionBodyExtraSchema).default([]),
  feedingPlans: z.array(CreateSubscriptionBodyFeedingPlanSchema).default([]),
  sharedFeedingPlan: CreateSubscriptionBodySharedFeedingPlanSchema.optional().nullable(),
})

export type CreateSubscriptionBody = z.input<typeof CreateSubscriptionBodySchema>

export const UpdateSubscriptionBodyExtraSchema = z.union([
  ExtraSchema
    .omit(UpdateEntityOmitFields)
    .partial({ id: true, version: true })
    .refine(idVersionMustNotExist, generateRequiredIdVersionError('Extra')),
  ExtraSchema
    .omit(UpdateEntityOmitFields)
    .partial()
    .required({ id: true, version: true }),
])

export type UpdateSubscriptionBodyExtra = z.infer<typeof UpdateSubscriptionBodyExtraSchema>

export const UpdateSubscriptionBodyFeedingPlanSkuSchema = z.union([
  FeedingPlanSkuSchema
    .omit(UpdateEntityOmitFields)
    .partial({ id: true, version: true })
    .refine(idVersionMustNotExist, generateRequiredIdVersionError('FeedingPlanSku')),
  FeedingPlanSkuSchema
    .omit(UpdateEntityOmitFields)
    .partial()
    .required({ id: true, version: true }),
])

export type UpdateSubscriptionBodyFeedingPlanSku = z.infer<typeof UpdateSubscriptionBodyFeedingPlanSkuSchema>

export const UpdateSubscriptionBodyFeedingPlanSchema = z.union([
  FeedingPlanSchema
    .omit(UpdateEntityOmitFields)
    .omit({
      currentPouchSizeInGrams: true,
      currentShippedDailyServingSizeInGrams: true,
      startOrderIndex: true,
    })
    .extend({
      skus: z.array(UpdateSubscriptionBodyFeedingPlanSkuSchema).min(1).optional(),
      startOrderIndex: z.number().int().min(0).optional(),
    })
    .partial({ id: true, version: true })
    .refine(idVersionMustNotExist, generateRequiredIdVersionError('FeedingPlan')),
  FeedingPlanSchema
    .omit(UpdateEntityOmitFields)
    .omit({
      currentPouchSizeInGrams: true,
      currentShippedDailyServingSizeInGrams: true,
    })
    .extend({
      skus: z.array(UpdateSubscriptionBodyFeedingPlanSkuSchema).min(1).optional(),
      startOrderIndex: z.number().int().min(0).optional(),
    })
    .partial()
    .required({ id: true, version: true }),
])

export type UpdateSubscriptionBodyFeedingPlan = z.infer<typeof UpdateSubscriptionBodyFeedingPlanSchema>

export const UpdateSubscriptionBodySharedFeedingPlanAnimalSchema = z.union([
  SharedFeedingPlanAnimalSchema
    .omit(UpdateEntityOmitFields)
    .partial({ id: true, version: true })
    .refine(idVersionMustNotExist, generateRequiredIdVersionError('SharedFeedingPlanAnimal')),
  SharedFeedingPlanAnimalSchema
    .omit(UpdateEntityOmitFields)
    .partial()
    .required({ id: true, version: true }),
])

export type UpdateSubscriptionBodySharedFeedingPlanAnimal = z.infer<typeof UpdateSubscriptionBodySharedFeedingPlanAnimalSchema>

export const UpdateSubscriptionBodySharedFeedingPlanSchema = z.union([
  SharedFeedingPlanSchema
    .omit(UpdateEntityOmitFields)
    .omit({
      startOrderIndex: true,
    })
    .extend({
      skus: z.array(UpdateSubscriptionBodyFeedingPlanSkuSchema).optional(),
      animals: z.array(UpdateSubscriptionBodySharedFeedingPlanAnimalSchema).optional(),
      startOrderIndex: z.number().int().min(0).optional(),
    })
    .partial({ id: true, version: true })
    .refine(idVersionMustNotExist, generateRequiredIdVersionError('SharedFeedingPlan')),
  SharedFeedingPlanSchema
    .omit(UpdateEntityOmitFields)
    .extend({
      skus: z.array(UpdateSubscriptionBodyFeedingPlanSkuSchema).optional(),
      animals: z.array(UpdateSubscriptionBodySharedFeedingPlanAnimalSchema).optional(),
      startOrderIndex: z.number().int().min(0).optional(),
    })
    .partial()
    .required({ id: true, version: true }),
])

export type UpdateSubscriptionBodySharedFeedingPlan = z.infer<typeof UpdateSubscriptionBodySharedFeedingPlanSchema>

export const UpdateSubscriptionBodySchema = SubscriptionSchema
  .partial()
  .pick({
    preferredDeliverySlotId: true,
    cadenceInWeeks: true,
    customCadenceInWeeks: true,
    customBoxInWeeks: true,
    feedingPlanTotalPrice: true,
    feedingPlanTotalPouches: true,
    note: true,
    planStrategy: true,
    priceStrategy: true,
  })
  .extend({
    version: SubscriptionSchema.shape.version,
    lastProcessedOrderIndex: z.number().int().gte(0).nullable().optional(),
    feedingPlans: z.array(UpdateSubscriptionBodyFeedingPlanSchema).optional(),
    sharedFeedingPlan: UpdateSubscriptionBodySharedFeedingPlanSchema.optional().nullable(),
    extras: z.array(UpdateSubscriptionBodyExtraSchema).optional(),
  })

export type UpdateSubscriptionBody = z.infer<typeof UpdateSubscriptionBodySchema>

// Index-only write. Separate from UpdateSubscriptionBodySchema because that one carries a
// version and is optimistic-lock checked, which is what made the charge workflows 409.
export const UpdateSubscriptionLastProcessedOrderIndexBodySchema = z.object({
  orderIndex: z.number().int().gte(0).describe('The index to write. By default stored only if greater than the current value.'),
  enforce: z.boolean().optional().default(false).describe('Write orderIndex as given instead of max(current, orderIndex). Use to correct drift downwards.'),
  skipVersionUpdate: z.boolean().optional().default(false).describe('Leave the subscription version untouched. Off by default: the row changed, so the version moves with it.'),
})

export type UpdateSubscriptionLastProcessedOrderIndexBody = z.infer<typeof UpdateSubscriptionLastProcessedOrderIndexBodySchema>

// Pre-parse shape, so callers can omit the flags that have defaults.
export type UpdateSubscriptionLastProcessedOrderIndexInput = z.input<typeof UpdateSubscriptionLastProcessedOrderIndexBodySchema>

export const UpdateSubscriptionLastProcessedOrderIndexResponseSchema = z.object({
  id: z.string().uuid(),
  lastProcessedOrderIndex: z.number().int().gte(0).describe('The index now stored.'),
  version: z.number().int().gte(0).describe('The version now stored. Unchanged only when skipVersionUpdate was set.'),
})

export type UpdateSubscriptionLastProcessedOrderIndexResponse = z.infer<typeof UpdateSubscriptionLastProcessedOrderIndexResponseSchema>

export const GetSubscriptionQuerySchema = z.object({
  includeIneligiblePlans: z.enum(['true', 'false']).optional().default('false'),
  includePausedPlans: z.enum(['true', 'false']).optional().default('false'),
  includeRemovedExtras: z.enum(['true', 'false']).optional().default('false'),
})

export type GetSubscriptionQuery = z.infer<typeof GetSubscriptionQuerySchema>

// One snapshot row from `subscriptions_history` — used by the order subscription-history
// backfill to resolve which historical snapshot an order was billed against.
export const SubscriptionHistoryFeedingPlanSchema = z.object({
  animalId: z.string().uuid(),
  currentDailyServingSizeInGrams: z.nativeEnum(FeedingPlanServingSize),
})

export type SubscriptionHistoryFeedingPlan = z.infer<typeof SubscriptionHistoryFeedingPlanSchema>

export const SubscriptionHistoryEntrySchema = z.object({
  id: z.string().uuid(),
  createdAt: z.coerce.date(),
  version: z.number().int().gte(0),
  feedingPlans: z.array(SubscriptionHistoryFeedingPlanSchema).describe('Per-animal serving sizes (full-bowl nominal) captured in this snapshot. Lyka only; empty for Tyga/shared.'),
})

export type SubscriptionHistoryEntry = z.infer<typeof SubscriptionHistoryEntrySchema>

export const GetSubscriptionHistoryResponseSchema = z.array(SubscriptionHistoryEntrySchema)

export type GetSubscriptionHistoryResponse = z.infer<typeof GetSubscriptionHistoryResponseSchema>

export const GetSubscriptionsByUserQuerySchema = z.object({
  userId: z.string().uuid(),
  includeIneligiblePlans: z.enum(['true', 'false']).optional().default('false'),
  includePausedPlans: z.enum(['true', 'false']).optional().default('false'),
  includeRemovedExtras: z.enum(['true', 'false']).optional().default('false'),
})

export type GetSubscriptionsByUserQuery = z.infer<typeof GetSubscriptionsByUserQuerySchema>

export const SearchSubscriptionsQuerySchema = z.object({
  animalId: z.string().uuid().optional(),
  /**
   * Selects which table an `animalId` lookup targets: lyka animals hang off `feeding_plans`,
   * tyga animals off `shared_feeding_plan_animals`. Defaults to lyka.
   */
  brand: z.nativeEnum(Brand).optional().default(Brand.LYKA),
  status: z.nativeEnum(SubscriptionStatus).optional(),
  includeIneligiblePlans: z.enum(['true', 'false']).optional().default('false'),
  includePausedPlans: z.enum(['true', 'false']).optional().default('false'),
  includeRemovedExtras: z.enum(['true', 'false']).optional().default('false'),
})

export type SearchSubscriptionsQuery = z.infer<typeof SearchSubscriptionsQuerySchema>

export const SubscriptionCadenceSchema = z.object({
  id: z.string().uuid(),
  status: z.nativeEnum(SubscriptionStatus),
  cadenceInWeeks: z.number().int().gte(0).nullable(),
  customCadenceInWeeks: z.number().int().gte(0).nullish(),
})

export type SubscriptionCadence = z.infer<typeof SubscriptionCadenceSchema>

export const SearchSubscriptionsByIdsBodySchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(500),
})

export type SearchSubscriptionsByIdsBody = z.infer<typeof SearchSubscriptionsByIdsBodySchema>

export const SearchSubscriptionsByIdsResponseSchema = z.object({
  data: z.array(SubscriptionCadenceSchema),
})

export type SearchSubscriptionsByIdsResponse = z.infer<typeof SearchSubscriptionsByIdsResponseSchema>

export const CurrentSubscriptionSearchBodySchema = z.object({
  animalIds: z.array(z.string().uuid()).max(500).optional(),
  userIds: z.array(z.string().uuid()).max(500).optional(),
}).refine(
  body => Boolean(body.animalIds?.length) || Boolean(body.userIds?.length),
  { message: 'At least one of animalIds or userIds must be a non-empty array' },
)

export type CurrentSubscriptionSearchBody = z.infer<typeof CurrentSubscriptionSearchBodySchema>

export const SlimSubscriptionSchema = z.object({
  id: z.string().uuid(),
  status: z.nativeEnum(SubscriptionStatus),
  userId: z.string().uuid(),
})

export type SlimSubscription = z.infer<typeof SlimSubscriptionSchema>

export const CurrentSubscriptionSearchResponseSchema = z.object({
  results: z.object({
    byAnimalIds: z.array(z.object({
      animalId: z.string().uuid(),
      foundSubscription: SlimSubscriptionSchema.nullable(),
    })).describe('One entry per requested animalId, in request order. Empty when animalIds is not supplied.'),
    byUserIds: z.array(z.object({
      userId: z.string().uuid(),
      foundSubscription: SlimSubscriptionSchema.nullable(),
    })).describe('One entry per requested userId, in request order. Empty when userIds is not supplied.'),
  }),
})

export type CurrentSubscriptionSearchResponse = z.infer<typeof CurrentSubscriptionSearchResponseSchema>

export const SubscriptionListItemSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  status: z.nativeEnum(SubscriptionStatus),
  brand: z.nativeEnum(Brand),
})

export type SubscriptionListItem = z.infer<typeof SubscriptionListItemSchema>

export const ListSubscriptionsQuerySchema = z.object({
  brand: z.nativeEnum(Brand).optional(),
  status: z.nativeEnum(SubscriptionStatus).optional(),
  cursor: z.string().uuid().optional().describe('Subscription id from the previous page; returns rows with id > cursor'),
  limit: z.coerce.number().int().positive().max(500).optional().default(100),
})

export type ListSubscriptionsQuery = z.infer<typeof ListSubscriptionsQuerySchema>

export const ListSubscriptionsResponseSchema = z.object({
  data: z.array(SubscriptionListItemSchema),
  count: z.number().int().nonnegative(),
  nextCursor: z.string().uuid().nullable().describe('Pass back as cursor for next page. null only when data is empty.'),
})

export type ListSubscriptionsResponse = z.infer<typeof ListSubscriptionsResponseSchema>

export type CancelSubscriptionSchema = z.infer<typeof CancelSubscriptionSchema>

export const CancelSubscriptionExtraBodySchema = z.object({
  cancelReasonId: z.string().uuid().optional(),
  cancelNotes: z.string().optional().nullable(),
  cancelledByAdmin: z.boolean().optional(),
})
export type CancelSubscriptionExtraBody = z.infer<typeof CancelSubscriptionExtraBodySchema>

export const CreateSubscriptionDiscountBodySchema = z.object({
  discountId: z.string().uuid(),
  discountInterval: z.nativeEnum(DiscountInterval),
  orderStartIndex: z.number().int().nonnegative(),
  orderQuantity: z.number().int().min(0).nullable(),
  createdBy: z.string().uuid(),
})
export type CreateSubscriptionDiscountBody = z.infer<typeof CreateSubscriptionDiscountBodySchema>

export const CreateSubscriptionDiscountResponseSchema = z.object({
  id: z.string().uuid(),
})
export type CreateSubscriptionDiscountResponse = z.infer<typeof CreateSubscriptionDiscountResponseSchema>

export const DeleteSubscriptionDiscountBodySchema = z.object({
  deletedBy: z.string().uuid(),
})
export type DeleteSubscriptionDiscountBody = z.infer<typeof DeleteSubscriptionDiscountBodySchema>

export const DeleteMigratedSubscriptionDiscountQueryParamsSchema = z.object({
  hardDelete: z.coerce.boolean().optional().default(false),
})

export type DeleteMigratedSubscriptionDiscountQueryParams = z.infer<typeof DeleteMigratedSubscriptionDiscountQueryParamsSchema>

export const DeleteSubscriptionPathParams = z.object({
  subscriptionId: z.string().uuid(),
  subscriptionDiscountId: z.string().uuid(),
})

export type DeleteSubscriptionPathParams = z.infer<typeof DeleteSubscriptionPathParams>

export const MigrateSubscriptionExtraSchema = ExtraSchema.omit({
  version: true,
}).extend({
  legacyCancelReasonId: z.number().int().optional().describe('Legacy laravel product subscription cancel reason id'),
})

export type MigrateSubscriptionExtraSchema = z.infer<typeof MigrateSubscriptionExtraSchema>

export const MigrateSubscriptionFeedingPlanSchema = FeedingPlanSchema.omit({
  version: true,
  currentShippedDailyServingSizeInGrams: true,
}).extend({
  skus: z.array(CreateSubscriptionBodyFeedingPlanSkuSchema).default([]),
})

export type MigrateSubscriptionFeedingPlanSchema = z.infer<typeof MigrateSubscriptionFeedingPlanSchema>

export const MigrateSubscriptionFeedingPlanHistorySchema = MigrateSubscriptionFeedingPlanSchema.omit({
  currentPouchSizeInGrams: true,
  startOrderIndex: true
}).extend({
  subscriptionId: z.string().uuid(),
  puppyMealPlan: z.boolean().default(false),
  puppyDailyServingSizeInGrams: z.nativeEnum(FeedingPlanServingSize).nullable(),
})

export type MigrateSubscriptionFeedingPlanHistorySchema = z.infer<typeof MigrateSubscriptionFeedingPlanHistorySchema>

export const MigrateSubscriptionDiscountSchema = SubscriptionDiscountSchema.extend({
  id: z.string().uuid().nullable(),
  createdBy: z.string().uuid().nullable().optional(),
  deletedBy: z.string().uuid().nullable().optional(),
  deletedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type MigrateSubscriptionDiscountSchema = z.infer<typeof MigrateSubscriptionDiscountSchema>

export const MigrateSubscriptionBodySchema = SubscriptionSchema.pick({
  id: true,
  preferredDeliverySlotId: true,
  cadenceInWeeks: true,
  customBoxInWeeks: true,
  customCadenceInWeeks: true,
  feedingPlanTotalPrice: true,
  feedingPlanTotalPouches: true,
  note: true,
  startAt: true,
  createdAt: true,
  lastProcessedOrderIndex: true,
  status: true,
  cancelledAt: true,
  pauseAt: true,
  introductorySubscriptionType: true,
}).extend({
  extras: z.array(MigrateSubscriptionExtraSchema).default([]),
  feedingPlans: z.array(MigrateSubscriptionFeedingPlanSchema).default([]),
  hasLastProcessedOrderInFulfillmentProcess: z.boolean(),
  discounts: z.array(MigrateSubscriptionDiscountSchema).default([]),
})

export type MigrateSubscriptionBodySchema = z.infer<typeof MigrateSubscriptionBodySchema>

export const SubscriptionDataMismatches = z.object({
  path: z.string(),
  expected: z.string().nullable(),
  actual: z.string().nullable(),
})

export type SubscriptionDataMismatches = z.infer<typeof SubscriptionDataMismatches>

export const SubscriptionSuccessResponseSchema = z.object({
  success: z.boolean(),
})

export type SubscriptionSuccessResponseSchema = z.infer<typeof SubscriptionSuccessResponseSchema>

export const VerifyMigratedSubscriptionResponseSchema = SubscriptionSuccessResponseSchema.extend({
  userId: z.string().uuid(),
  mismatches: SubscriptionDataMismatches.array(),
})

export type VerifyMigratedSubscriptionResponseSchema = z.infer<typeof VerifyMigratedSubscriptionResponseSchema>

export const BaseSubscriptionCancelReasonSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid().describe('UUID (resolved to subscription id in subscriptions)'),
  otherReason: z.string().nullable(),
  cancelledByAdmin: z.boolean(),
  cancelledDate: z.string().datetime(),
  reactivationDate: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export const BaseSubscriptionPauseReasonSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid().describe('UUID (resolved to subscription id in subscriptions)'),
  reasonOther: z.string().nullable(),
  pausedByAdmin: z.boolean(),
  pausedDate: z.string().datetime(),
  unpausedDate: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type BaseSubscriptionCancelReasonSchema = z.infer<typeof BaseSubscriptionCancelReasonSchema>

export const PartialMappedCancelReasonSchema = BaseSubscriptionCancelReasonSchema.extend({
  legacyReasonId: z.number().int(),
})
export type PartialMappedCancelReason = z.infer<typeof PartialMappedCancelReasonSchema>

/** Laravel `pausing_reasons_users` rows for legacy historical export / migration */
export const PartialMappedPauseReasonSchema = BaseSubscriptionPauseReasonSchema.extend({
  legacyReasonId: z.number().int(),
})

export type PartialMappedPauseReason = z.infer<typeof PartialMappedPauseReasonSchema>

export const MigrateSubscriptionPauseReasonsResponseSchema = BaseSubscriptionPauseReasonSchema.extend({
  subscriptionId: z.string().uuid(),
  reasonId: z.string().uuid().nullable(),
})

export type MigrateSubscriptionPauseReasonsResponseSchema = z.infer<typeof MigrateSubscriptionPauseReasonsResponseSchema>

export const MigrateSubscriptionCancelReasonsResponseSchema = BaseSubscriptionCancelReasonSchema.extend({
  subscriptionId: z.string().uuid(),
  primaryCancelReasonId: z.string().uuid().optional(),
  secondaryCancelReasonId: z.string().uuid().optional(),
})

export type MigrateSubscriptionCancelReasonsResponseSchema = z.infer<typeof MigrateSubscriptionCancelReasonsResponseSchema>

export const MigrateSubscriptionHistoryDataRequestSchema = z.object({
  dryRun: z.boolean().default(false),
  userId: z.string().uuid(),
  selectedHistoryTables: z.nativeEnum(HistoryTables).array().optional().describe('Subset of history tables to migrate. Defaults to all implemented tables.'),
  history: z.object({
    subscriptionCancelReasons: z.array(PartialMappedCancelReasonSchema),
    feedingPlanHistory: z.array(MigrateSubscriptionFeedingPlanHistorySchema),
    subscriptionPauseReasons: z.array(PartialMappedPauseReasonSchema),
  }),
})

export type MigrateSubscriptionHistoryDataRequest = z.infer<typeof MigrateSubscriptionHistoryDataRequestSchema>

export const VerifyMigratedHistoricalDataRequestSchema = MigrateSubscriptionHistoryDataRequestSchema.omit({
  dryRun: true,
})

export type VerifyMigratedHistoricalDataRequest = z.infer<typeof VerifyMigratedHistoricalDataRequestSchema>

export const MigrateSubscriptionHistoryResponseSchema = z.object({
  feedingPlanHistory: z.array(MigrateSubscriptionFeedingPlanHistorySchema),
  cancelReasons: z.array(MigrateSubscriptionCancelReasonsResponseSchema),
  pauseReasons: z.array(MigrateSubscriptionPauseReasonsResponseSchema),
})

export type MigrateSubscriptionHistoryResponse = z.infer<typeof MigrateSubscriptionHistoryResponseSchema>

/**
 * Repair of the self-referencing placeholders the pre-PR-#3631 Laravel -> Phoenix migration wrote into
 * the feeding-plan history tables. Driven one batch at a time by the
 * `backfillFeedingPlanHistoryIds` workflow so nothing runs as a single long transaction.
 */
export enum BackfillFeedingPlanHistoryPhase {
  /** "feeding_plans_history"."feeding_plan_id" = "id" -> the customer's current feeding plan. */
  feedingPlan = 'feeding_plan',
  /** "feeding_plan_skus_history"."feeding_plan_sku_id" = "feeding_plan_history_id" -> the real sku. */
  feedingPlanSku = 'feeding_plan_sku',
  nullDanglingFeedingPlan = 'null_dangling_feeding_plan',
  nullDanglingFeedingPlanSku = 'null_dangling_feeding_plan_sku',
}

export const BackfillFeedingPlanHistoryRequestSchema = z.object({
  phase: z.nativeEnum(BackfillFeedingPlanHistoryPhase).describe('Which table to repair. Phases run in order: feeding_plan must fully finish before feeding_plan_sku (sku resolution reads the repaired feeding_plan_id), and both must finish before the null_dangling_* phases, which clear only what repair could not resolve.'),
  cursor: z.string().uuid().nullable().optional().default(null).describe('Exclusive lower bound on the history row id. Null starts from the beginning.'),
  batchSize: z.number().int().min(1).max(20000).optional().default(2000).describe('Maximum history rows scanned in this batch.'),
  dryRun: z.boolean().optional().default(false).describe('Resolve and count without writing anything — no updates and no review rows.'),
})

export type BackfillFeedingPlanHistoryRequest = z.infer<typeof BackfillFeedingPlanHistoryRequestSchema>

export const BackfillFeedingPlanHistoryResponseSchema = z.object({
  phase: z.nativeEnum(BackfillFeedingPlanHistoryPhase),
  dryRun: z.boolean(),
  scanned: z.number().int().describe('Broken rows examined in this batch, before scope filtering.'),
  inScope: z.number().int().describe('Of those, rows attributable to the legacy migration on brand lyka.'),
  updated: z.number().int().describe('Rows repaired, or for the null_dangling_* phases, rows set to NULL.'),
  unresolved: z.number().int().describe('In-scope rows that could not be resolved; recorded for manual review. Always 0 for the null_dangling_* phases — every row in scope there is resolvable to NULL by definition.'),
  nextCursor: z.string().uuid().nullable().describe('Cursor to pass to the next batch. Null when the phase is complete.'),
  done: z.boolean().describe('True when no rows remain for this phase.'),
})

export type BackfillFeedingPlanHistoryResponse = z.infer<typeof BackfillFeedingPlanHistoryResponseSchema>


export const StarterBoxBackfillSubscriptionQuerySchema = z.object({
  cursor: z.string().uuid().optional().describe('Exclusive lower bound on subscription id. Omit to start from the beginning.'),
  limit: z.coerce.number().int().min(1).max(500).optional().default(100).describe('Maximum candidate subscriptions returned.'),
})

export type StarterBoxBackfillSubscriptionQuery = z.infer<typeof StarterBoxBackfillSubscriptionQuerySchema>

export const StarterBoxBackfillSubscriptionSchema = z.object({
  subscriptionId: z.string().uuid(),
  userId: z.string().uuid(),
})

export type StarterBoxBackfillSubscription = z.infer<typeof StarterBoxBackfillSubscriptionSchema>

export const StarterBoxBackfillSubscriptionResponseSchema = z.object({
  data: z.array(StarterBoxBackfillSubscriptionSchema),
  nextCursor: z.string().uuid().nullable().describe('Cursor for the next page. Null when the scan is complete.'),
})

export type StarterBoxBackfillSubscriptionResponse = z.infer<typeof StarterBoxBackfillSubscriptionResponseSchema>

export const BackfillStarterBoxFeedingPlansRequestSchema = z.object({
  userId: z.string().uuid(),
  feedingPlans: z.array(MigrateSubscriptionFeedingPlanSchema).min(1).describe('Starter box plans re-derived by the exporter. Inserted at startOrderIndex 0.'),
  dryRun: z.boolean().optional().default(false),
})

export type BackfillStarterBoxFeedingPlansRequest = z.infer<typeof BackfillStarterBoxFeedingPlansRequestSchema>

export enum StarterBoxBackfillOutcome {
  /** Starter box plans inserted; the user's existing plans were shifted to startOrderIndex 1. */
  backfilled = 'backfilled',
  // Already has ineligible plans
  alreadyPresent = 'alreadyPresent',
  // subscription not present
  noSubscription = 'noSubscription',
  // No animals matching, maybe they are removed
  noMatchingAnimals = 'noMatchingAnimals',
  // There is already starterOrderIndex for this animalId
  startOrderIndexOccupied = 'startOrderIndexOccupied',
}

export const BackfillStarterBoxFeedingPlansResponseSchema = z.object({
  userId: z.string().uuid(),
  subscriptionId: z.string().uuid().nullable(),
  outcome: z.nativeEnum(StarterBoxBackfillOutcome),
  dryRun: z.boolean(),
  feedingPlansCreated: z.number().int(),
  plansShifted: z.number().int().describe('Existing plans moved from startOrderIndex 0 to 1 to make room for the starter box.'),
})

export type BackfillStarterBoxFeedingPlansResponse = z.infer<typeof BackfillStarterBoxFeedingPlansResponseSchema>

export const FilterAnimalIdsByStatusBodySchema = z.object({
  animalIds: z.array(z.string().uuid()).max(2000),
  brand: z.nativeEnum(Brand),
  subscriptionStatus: z.nativeEnum(SubscriptionStatus),
  planStatus: z.nativeEnum(FeedingPlanStatus),
})

export type FilterAnimalIdsByStatusBody = z.infer<typeof FilterAnimalIdsByStatusBodySchema>

export const FilterAnimalIdsByStatusResponseSchema = z.object({
  animalIds: z.array(z.string().uuid()).describe('The supplied animals on a subscription for the requested brand whose subscription and feeding plan match the given statuses.'),
})

export type FilterAnimalIdsByStatusResponse = z.infer<typeof FilterAnimalIdsByStatusResponseSchema>
